import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Users, BookCheck, PenLine, AlertTriangle, Loader2, TrendingUp, ChevronRight, Printer,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { EvalChart } from "@/lib/evalSystem";
import { TeacherShell } from "@/components/TeacherShell";

function StatCard({ icon: Icon, label, value, tone = "slate", testid }) {
  const tones = {
    slate: "text-slate-900 dark:text-slate-100",
    emerald: "text-emerald-600",
    sky: "text-sky-600",
    amber: "text-amber-600",
    rose: "text-rose-600",
  };
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)] dark:border-white/10 dark:bg-white/[0.03]" data-testid={testid}>
      <div className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-400">
        <Icon size={15} /> {label}
      </div>
      <p className={`text-3xl font-semibold tracking-tight ${tones[tone]}`}>{value}</p>
    </div>
  );
}

function StudentAlert({ title, students, tone, navigate, testid }) {
  const border = tone === "rose" ? "border-rose-100 dark:border-rose-500/20" : "border-amber-100 dark:border-amber-500/20";
  const dot = tone === "rose" ? "bg-rose-500" : "bg-orange-400";
  return (
    <section className={`rounded-2xl border ${border} bg-white p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)] dark:bg-white/[0.03]`} data-testid={testid}>
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
        <AlertTriangle size={16} className={tone === "rose" ? "text-rose-500" : "text-orange-400"} /> {title} ({students.length})
      </h3>
      {students.length ? (
        <div className="flex flex-col gap-1.5">
          {students.map((s) => (
            <button
              key={s.id}
              onClick={() => navigate(`/enseignant/eleve/${s.id}`)}
              className="flex items-center justify-between rounded-xl border border-slate-100 px-3 py-2 text-left text-sm text-slate-700 transition-transform hover:scale-[0.99] dark:border-white/10 dark:text-slate-200"
              data-testid={`alert-student-${s.id}`}
            >
              <span className="flex items-center gap-2"><span className={`h-2 w-2 rounded-full ${dot}`} /> {s.name}</span>
              <ChevronRight size={15} className="text-slate-300" />
            </button>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-400">Aucun élève concerné. 👍</p>
      )}
    </section>
  );
}

export default function ClassDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/classes/${id}/dashboard`);
      setData(res.data);
    } catch {
      toast.error("Chargement du tableau de bord impossible.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  return (
    <TeacherShell active="dashboard" contentClassName="w-full">
      <header className="sticky top-0 z-10 border-b border-slate-100 bg-white/85 backdrop-blur-xl dark:border-white/10 dark:bg-[#0A0A0A]/85">
        {/* CD-HEADER */}
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-3">
          <button
            onClick={() => navigate(`/enseignant/classe/${id}`)}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-transform hover:scale-95 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
            data-testid="dashboard-back" aria-label="Retour au tableau"
          >
            <ArrowLeft size={18} />
          </button>
          <h1 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-100">
            Tableau de bord{data?.class?.name ? ` — ${data.class.name}` : ""}
          </h1>
          <button
            onClick={() => window.print()}
            className="ml-auto flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 transition-transform hover:scale-95 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
            data-testid="print-dashboard-button"
          >
            <Printer size={16} /> <span className="hidden sm:inline">Imprimer / PDF</span>
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-6">
        {loading ? (
          <div className="flex justify-center py-24" data-testid="dashboard-loading">
            <Loader2 className="animate-spin text-slate-400" size={28} />
          </div>
        ) : !data ? null : (
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="flex flex-col gap-6"
          >
            <h1 className="hidden text-2xl font-semibold text-slate-900 print:block">
              Tableau de bord — {data.class?.name}
            </h1>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="stat-cards">
              <StatCard icon={Users} label="Élèves" value={data.student_count} testid="stat-students" />
              <StatCard icon={BookCheck} label="Semaines publiées" value={data.published_count} tone="sky" testid="stat-published" />
              <StatCard icon={PenLine} label="Taux de signature" value={`${data.signature_rate}%`} tone="emerald" testid="stat-signature" />
              <StatCard
                icon={PenLine}
                label={data.current_week_published ? "Signatures (semaine)" : "Semaine non publiée"}
                value={data.current_week_published ? `${data.current_week_rate}%` : "—"}
                tone="sky" testid="stat-current-week"
              />
            </div>

            <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)] dark:border-white/10 dark:bg-white/[0.03]" data-testid="evolution-section">
              <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                <TrendingUp size={16} className="text-slate-400" /> Évolution globale de la classe
              </h3>
              {data.evolution.length < 2 ? (
                <p className="py-10 text-center text-sm text-slate-400">Pas assez de semaines pour tracer une évolution.</p>
              ) : (
                <div data-testid="class-evolution-chart">
                  <EvalChart data={data.evolution} criteria={data.criteria} scale={data.chart_scale} height={288} />
                </div>
              )}
            </section>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <StudentAlert title={data.eval_system?.type === "passeport" ? "Élèves avec au moins un orange" : "Élèves à renforcer"} students={data.orange_students} tone="amber" navigate={navigate} testid="orange-students" />
              <StudentAlert title={data.eval_system?.type === "passeport" ? "Élèves avec au moins un rouge" : "Élèves en difficulté"} students={data.red_students} tone="rose" navigate={navigate} testid="red-students" />
            </div>
          </motion.div>
        )}
      </main>
    </TeacherShell>
  );
}
